package com.tregz.tremenz.ui.screen

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.wrapContentWidth
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.GridItemSpan
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.foundation.selection.toggleable
import androidx.compose.ui.semantics.Role
import androidx.compose.material3.Switch
import androidx.compose.material3.SwitchDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.unit.dp
import com.tregz.tremenz.io.cloud.CloudPath
import com.tregz.tremenz.io.model.ModelData
import com.tregz.tremenz.io.model.ModelRepository
import com.tregz.tremenz.ui.component.CardMedia
import com.tregz.tremenz.ui.component.StatusNotice
import com.tregz.tremenz.ui.icon.IconKey
import com.tregz.tremenz.ui.icon.iconPainter
import com.tregz.tremenz.ui.label.LabelKey
import com.tregz.tremenz.ui.label.labelText

/**
 * The Playlists destination: the Apero / Bistro / Club catalogue, filtered to either its audio or
 * its video variant.
 */
@Composable
fun ScreenLibrary(
    currentMedia: ModelData.Media?,
    isVideoPlaying: Boolean,
    isPreparingVideo: Boolean,
    onPlayMedia: (ModelData.Media) -> Unit,
    onOpenLink: (String) -> Unit = {},
    modifier: Modifier = Modifier
) {
    val scheme = MaterialTheme.colorScheme
    val itemsList by ModelRepository.LIVE.collectAsState()
    val status by ModelRepository.MIXES_STATUS.collectAsState()

    var isAudioOnly by rememberSaveable { mutableStateOf(false) }
    var isAperoExpanded by rememberSaveable { mutableStateOf(true) }
    var isBistroExpanded by rememberSaveable { mutableStateOf(true) }
    var isClubExpanded by rememberSaveable { mutableStateOf(true) }
    var isOtherExpanded by rememberSaveable { mutableStateOf(true) }

    // Filter playlists by audio only switch
    val currentPlaylists = remember(itemsList, isAudioOnly) {
        if (isAudioOnly) {
            itemsList.filterIsInstance<ModelData.Audio>()
        } else {
            itemsList.filterIsInstance<ModelData.Video>()
        }
    }

    val aperoPlaylists = remember(currentPlaylists) {
        currentPlaylists.filter {
            it.genre.equals(CloudPath.APERO.name, ignoreCase = true)
        }
    }
    val bistroPlaylists = remember(currentPlaylists) {
        currentPlaylists.filter {
            it.genre.equals(CloudPath.BISTRO.name, ignoreCase = true)
        }
    }
    val clubPlaylists = remember(currentPlaylists) {
        currentPlaylists.filter {
            it.genre.equals(CloudPath.CLUB.name, ignoreCase = true)
        }
    }
    // A collection the sections do not know would otherwise vanish without a trace.
    val otherPlaylists = remember(currentPlaylists) {
        currentPlaylists.filter { media ->
            CloudPath.entries.none { media.genre.equals(it.name, ignoreCase = true) }
        }
    }
    val hasMedia = remember(itemsList) { itemsList.any { it is ModelData.Media } }

    // Cards stay one per row until the pane can hold two of them side by side.
    LazyVerticalGrid(
        columns = GridCells.Adaptive(minSize = 400.dp),
        modifier = modifier
            .fillMaxWidth()
            .wrapContentWidth(Alignment.CenterHorizontally)
            .widthIn(max = 1200.dp)
    ) {
        // Header: Audio only switch
        item(span = { GridItemSpan(maxLineSpan) }) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .toggleable(
                        value = isAudioOnly,
                        role = Role.Switch,
                        onValueChange = { isAudioOnly = it }
                    )
                    .padding(horizontal = 16.dp, vertical = 8.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Text(
                    text = labelText(LabelKey.CONTROL_AUDIO_ONLY),
                    color = scheme.onBackground,
                    style = MaterialTheme.typography.titleMedium
                )
                Switch(
                    checked = isAudioOnly,
                    onCheckedChange = null,
                    colors = SwitchDefaults.colors(
                        checkedThumbColor = scheme.onPrimary,
                        checkedTrackColor = scheme.primary
                    )
                )
            }
        }

        // Nothing to list yet: say whether it is still loading, failed or simply empty.
        if (!hasMedia) {
            item(span = { GridItemSpan(maxLineSpan) }) {
                StatusNotice(status = status, onRetry = ModelRepository::retry)
            }
        }

        // Section 1: Apero
        item(span = { GridItemSpan(maxLineSpan) }) {
            SectionHeaderRow(
                title = labelText(LabelKey.CHANNEL_APERO),
                icon = IconKey.WINE,
                count = aperoPlaylists.size,
                isExpanded = isAperoExpanded,
                onClick = { isAperoExpanded = !isAperoExpanded }
            )
        }
        if (isAperoExpanded) {
            items(aperoPlaylists, key = { playlistKey(it) }) { item ->
                PlaylistCard(item, currentMedia, isVideoPlaying, isPreparingVideo, onPlayMedia, onOpenLink)
            }
        }

        // Section 2: Bistro
        item(span = { GridItemSpan(maxLineSpan) }) {
            SectionHeaderRow(
                title = labelText(LabelKey.CHANNEL_BISTRO),
                icon = IconKey.BEER,
                count = bistroPlaylists.size,
                isExpanded = isBistroExpanded,
                onClick = { isBistroExpanded = !isBistroExpanded }
            )
        }
        if (isBistroExpanded) {
            items(bistroPlaylists, key = { playlistKey(it) }) { item ->
                PlaylistCard(item, currentMedia, isVideoPlaying, isPreparingVideo, onPlayMedia, onOpenLink)
            }
        }

        // Section 3: Club
        item(span = { GridItemSpan(maxLineSpan) }) {
            SectionHeaderRow(
                title = labelText(LabelKey.CHANNEL_CLUB),
                icon = IconKey.COCKTAIL,
                count = clubPlaylists.size,
                isExpanded = isClubExpanded,
                onClick = { isClubExpanded = !isClubExpanded }
            )
        }
        if (isClubExpanded) {
            items(clubPlaylists, key = { playlistKey(it) }) { item ->
                PlaylistCard(item, currentMedia, isVideoPlaying, isPreparingVideo, onPlayMedia, onOpenLink)
            }
        }

        // Section 4: whatever the three above do not claim
        if (otherPlaylists.isNotEmpty()) {
            item(span = { GridItemSpan(maxLineSpan) }) {
                SectionHeaderRow(
                    title = labelText(LabelKey.CHANNEL_OTHER),
                    icon = IconKey.MUSIC,
                    count = otherPlaylists.size,
                    isExpanded = isOtherExpanded,
                    onClick = { isOtherExpanded = !isOtherExpanded }
                )
            }
            if (isOtherExpanded) {
                items(otherPlaylists, key = { playlistKey(it) }) { item ->
                    PlaylistCard(item, currentMedia, isVideoPlaying, isPreparingVideo, onPlayMedia, onOpenLink)
                }
            }
        }
    }
}

/** Stable across reorders; the collection is part of it because document ids repeat between collections. */
private fun playlistKey(media: ModelData.Media): String =
    "${if (media is ModelData.Audio) "audio" else "video"}/${media.path.lowercase()}/${media.id}"

@Composable
private fun PlaylistCard(
    item: ModelData.Media,
    currentMedia: ModelData.Media?,
    isVideoPlaying: Boolean,
    isPreparingVideo: Boolean,
    onPlayMedia: (ModelData.Media) -> Unit,
    onOpenLink: (String) -> Unit
) {
    CardMedia(
        media = item,
        isPlaying = currentMedia == item && (if (item is ModelData.Video) isVideoPlaying else true),
        isPreparing = currentMedia == item && item is ModelData.Video && isPreparingVideo,
        onPlayClick = { onPlayMedia(item) },
        onOpenLink = onOpenLink
    )
}

@Composable
private fun SectionHeaderRow(
    title: String,
    icon: IconKey,
    count: Int,
    isExpanded: Boolean,
    onClick: () -> Unit
) {
    val scheme = MaterialTheme.colorScheme
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 12.dp, vertical = 4.dp)
            .clip(RoundedCornerShape(8.dp))
            .background(scheme.surfaceVariant)
            .clickable(onClick = onClick)
            .padding(horizontal = 16.dp, vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(
                painter = iconPainter(icon),
                contentDescription = title,
                tint = scheme.primary,
                modifier = Modifier.size(24.dp)
            )
            Spacer(modifier = Modifier.width(12.dp))
            Text(
                text = title,
                color = scheme.onSurface,
                style = MaterialTheme.typography.titleMedium
            )
            Spacer(modifier = Modifier.width(8.dp))
            Text(
                text = "($count)",
                color = scheme.outline,
                style = MaterialTheme.typography.bodyMedium
            )
        }
        Icon(
            painter = iconPainter(if (isExpanded) IconKey.COLLAPSE else IconKey.EXPAND),
            contentDescription = labelText(if (isExpanded) LabelKey.MEDIA_COLLAPSE else LabelKey.MEDIA_EXPAND),
            tint = scheme.onSurfaceVariant
        )
    }
}
